import React, { useState, useRef, useEffect } from "react";
import { ArrowUpDown, SlidersHorizontal } from "lucide-react";

const SORT_OPTIONS = [
  { value: "most-popular", label: "Most popular", sub: "Our top picks" },
  { value: "price-low", label: "Low to high", sub: null },
  { value: "price-high", label: "High to low", sub: null },
];

const FilterOptions = ({
  totalCars = 0,
  filteredCars = 0,
  sortBy,
  onSortChange,
  onMobileFilterOpen,
  activeFilterCount = 0,
  vehicleResults = [],
  currencyCode = "",
}) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Compute cheapest + most expensive price for tab sub-labels
  const prices = vehicleResults
    .map((v) => v.primaryOffer?.totalPrice)
    .filter(Boolean);
  const minPrice = prices.length ? Math.min(...prices) : null;
  const maxPrice = prices.length ? Math.max(...prices) : null;

  const formatPrice = (amount) => {
    if (!amount) return "";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode || "USD",
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const getSub = (opt) => {
    if (opt.value === "price-low" && minPrice)
      return `from ${formatPrice(minPrice)}`;
    if (opt.value === "price-high" && maxPrice)
      return `up to ${formatPrice(maxPrice)}`;
    return opt.sub;
  };

  const currentOption =
    SORT_OPTIONS.find((o) => o.value === sortBy) || SORT_OPTIONS[0];

  if (totalCars === 0) return null;

  return (
    <div className="mb-4">
      <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between mb-4 gap-3">
        {/* Count + subtitle */}
        <div>
          <h2 className="text-xl lg:text-2xl font-heading font-bold text-text">
            {filteredCars} {filteredCars === 1 ? "car" : "cars"} available
          </h2>
          <p className="text-xs lg:text-sm text-text-muted mt-0.5">
            Prices include taxes and fees · sorted by{" "}
            <span className="font-semibold text-text">
              {currentOption.label.toLowerCase()}
            </span>
          </p>
        </div>

        {/* Buttons — always one line */}
        <div className="flex items-center gap-2 shrink-0">
          {/* Mobile filter button */}
          <button
            onClick={onMobileFilterOpen}
            className="lg:hidden flex-1 flex items-center gap-2 border border-surface-border bg-white rounded-xl px-4 py-2.5 text-sm font-semibold text-text shadow-sm"
          >
            <SlidersHorizontal className="w-4 h-4 text-primary" />
            Filters
            {activeFilterCount > 0 && (
              <span className="bg-primary text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                {activeFilterCount}
              </span>
            )}
          </button>

          {/* Sort dropdown */}
          <div className="relative flex-1" ref={dropdownRef}>
            <button
              onClick={() => setDropdownOpen((o) => !o)}
              className="flex items-center gap-2 border border-surface-border bg-white rounded-xl px-4 py-2.5 text-sm font-semibold text-text shadow-sm hover:border-primary/40 transition-colors whitespace-nowrap"
            >
              <ArrowUpDown className="w-4 h-4 text-text-muted shrink-0" />
              <span>Sort: {currentOption.label}</span>
            </button>

            {dropdownOpen && (
              <div className="absolute right-0 top-full mt-2 w-52 bg-white border border-surface-border rounded-xl shadow-card z-50 overflow-hidden">
                <div className="px-4 py-3 border-b border-surface-border">
                  <span className="text-xs font-bold text-text-muted uppercase tracking-wider">
                    Sort by
                  </span>
                </div>
                {SORT_OPTIONS.map((opt) => {
                  const sub = getSub(opt);
                  const isActive = sortBy === opt.value;
                  return (
                    <button
                      key={opt.value}
                      onClick={() => {
                        onSortChange?.(opt.value);
                        setDropdownOpen(false);
                      }}
                      className={`w-full text-left px-4 py-3 transition-colors ${
                        isActive
                          ? "bg-accent text-primary"
                          : "hover:bg-surface-raised text-text"
                      }`}
                    >
                      <div
                        className={`text-sm font-semibold ${isActive ? "text-primary" : "text-text"}`}
                      >
                        {opt.label}
                      </div>
                      {sub && (
                        <div
                          className={`text-xs mt-0.5 ${isActive ? "text-primary/70" : "text-text-muted"}`}
                        >
                          {sub}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FilterOptions;
